import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { cssVars } from '../../shared/lib/styledTheme';
import {
  banHudleUser,
  deleteHudleAvatar,
  disableHudleMfa,
  patchHudleProfile,
  resetHudlePassword,
  unbanHudleUser,
} from '../../entities/client/api/hudleStaffApi';

interface HudleStaffActionsProps {
  clientId: string;
  uid?: string;
  externalSite?: string;
  email?: string;
  isBanned?: boolean;
  ticketId?: string;
  variant?: 'tools' | 'compact';
}

type ModalKind = 'reset' | 'mfa' | 'profile' | 'bio' | 'ban' | 'unban' | 'avatar' | null;

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.error || error?.response?.data?.detail || error?.message || fallback;

export const HudleStaffActions: React.FC<HudleStaffActionsProps> = ({
  clientId,
  uid,
  externalSite,
  email,
  isBanned = false,
  ticketId,
  variant = 'tools',
}) => {
  const [modal, setModal] = useState<ModalKind>(null);
  const [busy, setBusy] = useState(false);
  const [username, setUsername] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [customStatus, setCustomStatus] = useState('');
  const [banReason, setBanReason] = useState('');

  const visible = Boolean(uid) && (externalSite || 'hudle') === 'hudle';
  const Button = variant === 'compact' ? CompactButton : ToolButton;

  const title = useMemo(() => {
    switch (modal) {
      case 'reset':
        return 'Сброс пароля';
      case 'mfa':
        return 'Снять 2FA';
      case 'profile':
        return 'Изменить данные';
      case 'bio':
        return 'Bio / статус';
      case 'ban':
        return 'Забанить аккаунт';
      case 'unban':
        return 'Разбанить аккаунт';
      case 'avatar':
        return 'Удалить аватар';
      default:
        return '';
    }
  }, [modal]);

  if (!visible) {
    return null;
  }

  const run = async (action: () => Promise<unknown>, success: string) => {
    try {
      setBusy(true);
      await action();
      alert(success);
      setModal(null);
    } catch (error) {
      alert(errorMessage(error, 'Не удалось выполнить действие'));
    } finally {
      setBusy(false);
    }
  };

  const submitModal = async () => {
    if (modal === 'reset') {
      await run(
        () => resetHudlePassword(clientId, email, ticketId),
        email
          ? `Ссылка для сброса пароля отправлена на ${email}`
          : 'Ссылка для сброса пароля отправлена'
      );
      return;
    }
    if (modal === 'mfa') {
      await run(() => disableHudleMfa(clientId, ticketId), '2FA снята, сессии отозваны');
      return;
    }
    if (modal === 'avatar') {
      await run(() => deleteHudleAvatar(clientId, ticketId), 'Аватар удалён');
      return;
    }
    if (modal === 'unban') {
      await run(() => unbanHudleUser(clientId, ticketId), 'Аккаунт разбанен');
      return;
    }
    if (modal === 'ban') {
      if (!banReason.trim()) {
        alert('Укажите причину бана');
        return;
      }
      await run(() => banHudleUser(clientId, banReason.trim(), ticketId), 'Аккаунт забанен');
      return;
    }
    if (modal === 'profile') {
      const data: { email?: string; username?: string; phone?: string } = {};
      if (username.trim()) data.username = username.trim();
      if (profileEmail.trim()) data.email = profileEmail.trim();
      if (phone.trim()) data.phone = phone.trim();
      if (!data.email && !data.username && !data.phone) {
        alert('Укажите хотя бы одно поле');
        return;
      }
      await run(() => patchHudleProfile(clientId, data, ticketId), 'Данные обновлены');
      return;
    }
    if (modal === 'bio') {
      await run(
        () => patchHudleProfile(clientId, { bio, customStatus }, ticketId),
        'Bio и статус обновлены'
      );
    }
  };

  return (
    <>
      <Button type="button" onClick={() => setModal('reset')} disabled={busy || !email} title={!email ? 'В CRM нет email клиента' : undefined}>
        Сбросить пароль
      </Button>
      <Button type="button" onClick={() => setModal('mfa')} disabled={busy}>
        Снять 2FA
      </Button>
      <Button type="button" onClick={() => { setProfileEmail(email || ''); setUsername(''); setPhone(''); setModal('profile'); }} disabled={busy}>
        Изменить данные
      </Button>
      {isBanned ? (
        <Button type="button" onClick={() => setModal('unban')} disabled={busy}>
          Разбанить
        </Button>
      ) : (
        <Button type="button" onClick={() => { setBanReason(''); setModal('ban'); }} disabled={busy}>
          Забанить
        </Button>
      )}
      <Button type="button" onClick={() => setModal('avatar')} disabled={busy}>
        Удалить аватар
      </Button>
      <Button type="button" onClick={() => { setBio(''); setCustomStatus(''); setModal('bio'); }} disabled={busy}>
        Bio / статус
      </Button>

      {modal && (
        <Overlay onClick={() => !busy && setModal(null)}>
          <Dialog onClick={event => event.stopPropagation()}>
            <DialogTitle>{title}</DialogTitle>
            {modal === 'reset' && (
              <Text>Отправить ссылку для смены пароля на <b>{email}</b>? Пароль в CRM не показывается — пользователь задаст его сам.</Text>
            )}
            {modal === 'mfa' && <Text>Снять двухфакторную аутентификацию и отозвать сессии пользователя?</Text>}
            {modal === 'avatar' && <Text>Удалить аватар пользователя в Hudle?</Text>}
            {modal === 'unban' && <Text>Разбанить аккаунт пользователя в Hudle?</Text>}
            {modal === 'ban' && (
              <>
                <Text>Укажите причину бана. Пользователь сразу будет разлогинен.</Text>
                <Field value={banReason} onChange={event => setBanReason(event.target.value)} placeholder="Причина" />
              </>
            )}
            {modal === 'profile' && (
              <>
                <Label>Username</Label>
                <Field value={username} onChange={event => setUsername(event.target.value)} placeholder="Не менять" />
                <Label>Email</Label>
                <Field value={profileEmail} onChange={event => setProfileEmail(event.target.value)} placeholder="Не менять" />
                <Label>Телефон</Label>
                <Field value={phone} onChange={event => setPhone(event.target.value)} placeholder="Не менять / пусто чтобы очистить" />
              </>
            )}
            {modal === 'bio' && (
              <>
                <Label>Bio</Label>
                <Area value={bio} onChange={event => setBio(event.target.value)} placeholder="Пусто — очистить" />
                <Label>Custom status</Label>
                <Field value={customStatus} onChange={event => setCustomStatus(event.target.value)} placeholder="Пусто — очистить" />
              </>
            )}
            <Actions>
              <Ghost type="button" onClick={() => setModal(null)} disabled={busy}>Отмена</Ghost>
              <Primary type="button" onClick={submitModal} disabled={busy}>
                {busy ? '...' : 'Подтвердить'}
              </Primary>
            </Actions>
          </Dialog>
        </Overlay>
      )}
    </>
  );
};

const ToolButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  background-color: ${cssVars.bgSecondary};
  border: 1px solid ${cssVars.borderColor};
  border-radius: 5px;
  cursor: pointer;
  min-height: 50px;
  font-size: 10px;
  font-weight: 500;
  color: ${cssVars.textPrimary};

  &:hover:not(:disabled) {
    background-color: ${cssVars.hover};
    border-color: ${cssVars.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CompactButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 6px;
  background-color: ${cssVars.bgSecondary};
  border: 1px solid ${cssVars.borderColor};
  border-radius: 4px;
  cursor: pointer;
  min-height: 28px;
  font-size: 10px;
  color: ${cssVars.textPrimary};

  &:hover:not(:disabled) {
    background-color: ${cssVars.hover};
    border-color: ${cssVars.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
`;

const Dialog = styled.div`
  width: 90%;
  max-width: 420px;
  background: ${cssVars.bgSecondary};
  border-radius: 8px;
  padding: 20px;
`;

const DialogTitle = styled.h3`
  margin: 0 0 12px;
  color: ${cssVars.textPrimary};
`;

const Text = styled.p`
  margin: 0 0 12px;
  color: ${cssVars.textPrimary};
  font-size: 14px;
  line-height: 1.4;
`;

const Label = styled.label`
  display: block;
  margin: 8px 0 4px;
  font-size: 12px;
  color: ${cssVars.textSecondary};
`;

const Field = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid ${cssVars.borderColor};
  border-radius: 6px;
  background: ${cssVars.bgColor};
  color: ${cssVars.textPrimary};
`;

const Area = styled.textarea`
  width: 100%;
  min-height: 80px;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid ${cssVars.borderColor};
  border-radius: 6px;
  background: ${cssVars.bgColor};
  color: ${cssVars.textPrimary};
  resize: vertical;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

const Ghost = styled.button`
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid ${cssVars.borderColor};
  background: transparent;
  color: ${cssVars.textPrimary};
  cursor: pointer;
`;

const Primary = styled.button`
  padding: 8px 12px;
  border-radius: 6px;
  border: none;
  background: ${cssVars.primary};
  color: ${cssVars.white};
  cursor: pointer;
`;
